/**
 * Multi-project local storage.
 * Each project holds a full ProjectBundle (skin, editor, undo, chat).
 * AI API keys remain global in localStorage (not per-project).
 */

import {
  applyProjectSnapshot,
  captureProjectBundle,
  parseProjectBundleJson,
  serializeProjectBundle,
} from "../project/projectBundle";
import { createDefaultSkin } from "../skin/defaultSkins";
import { createBlankSkin } from "../skin/document";
import { useChatSessionStore } from "../store/chat-session-store";
import { useSkinStore } from "../store/skin-store";
import { restoreDraft } from "./draft";
import { WORKSPACE_KEY, WorkspacePersistenceError } from "./projects-constants";

const DATABASE_NAME = "skinpilot";
/** v3: multi-project store. */
const DATABASE_VERSION = 3;
const PROJECTS_STORE = "projects";
const WORKSPACE_STORE = "workspace";
const DRAFTS_STORE = "drafts";

export const ACTIVE_PROJECT_STORAGE_KEY = "skinpilot.activeProjectId";

export type ProjectMeta = {
  id: string;
  name: string;
  createdAt: number;
  updatedAt: number;
};

type ProjectRecord = ProjectMeta & {
  /** Serialized ProjectBundle JSON. */
  json: string;
};

export class ProjectPersistenceError extends Error {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = "ProjectPersistenceError";
  }
}

type PersistActiveProjectOptions = {
  activate?: boolean;
};

const projectPersistTails = new Map<string, Promise<void>>();
const projectsPendingDeletion = new Set<string>();

async function enqueueProjectPersist<T>(
  projectId: string,
  task: () => Promise<T>,
): Promise<T> {
  const previous = projectPersistTails.get(projectId) ?? Promise.resolve();
  const result = previous.catch(() => undefined).then(task);
  const tail = result.then(
    () => undefined,
    () => undefined,
  );
  projectPersistTails.set(projectId, tail);
  try {
    return await result;
  } finally {
    if (projectPersistTails.get(projectId) === tail) {
      projectPersistTails.delete(projectId);
    }
  }
}

function createId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `proj-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

function canUseLocalStorage(): boolean {
  return typeof window !== "undefined" && "localStorage" in window;
}

export function getActiveProjectId(): string | null {
  if (!canUseLocalStorage()) return null;
  try {
    return window.localStorage.getItem(ACTIVE_PROJECT_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setActiveProjectId(id: string): void {
  if (!canUseLocalStorage()) return;
  try {
    window.localStorage.setItem(ACTIVE_PROJECT_STORAGE_KEY, id);
  } catch {
    // Ignore quota errors; in-memory active id still works for the session.
  }
}

function restoreActiveProjectId(id: string | null): void {
  if (!canUseLocalStorage()) return;
  try {
    if (id) {
      window.localStorage.setItem(ACTIVE_PROJECT_STORAGE_KEY, id);
    } else {
      window.localStorage.removeItem(ACTIVE_PROJECT_STORAGE_KEY);
    }
  } catch {
    // Ignore storage errors; the project library keeps an in-memory active id.
  }
}

async function rollbackFailedProject(
  failedProjectId: string,
  previousActiveId: string | null,
  reloadPreviousWorkspace: boolean,
): Promise<void> {
  projectsPendingDeletion.add(failedProjectId);
  try {
    await enqueueProjectPersist(failedProjectId, () =>
      deleteProjectRecord(failedProjectId),
    );
  } catch {
    projectsPendingDeletion.delete(failedProjectId);
  }

  if (reloadPreviousWorkspace && previousActiveId) {
    try {
      await loadProjectIntoApp(previousActiveId);
      return;
    } catch {
      // Fall back to restoring the id even if the previous record is damaged.
    }
  }
  restoreActiveProjectId(previousActiveId);
}

export async function listProjects(): Promise<ProjectMeta[]> {
  const database = await openDatabase();
  try {
    const records = await requestAsPromise<ProjectRecord[]>(
      database
        .transaction(PROJECTS_STORE, "readonly")
        .objectStore(PROJECTS_STORE)
        .getAll(),
    );
    return records
      .map((record) => ({
        id: record.id,
        name: record.name,
        createdAt: record.createdAt,
        updatedAt: record.updatedAt,
      }))
      .sort((left, right) => right.updatedAt - left.updatedAt);
  } finally {
    database.close();
  }
}

export async function getProject(id: string): Promise<ProjectRecord | null> {
  const database = await openDatabase();
  try {
    const record = await requestAsPromise<ProjectRecord | undefined>(
      database
        .transaction(PROJECTS_STORE, "readonly")
        .objectStore(PROJECTS_STORE)
        .get(id),
    );
    return record ?? null;
  } finally {
    database.close();
  }
}

export async function saveProjectRecord(record: ProjectRecord): Promise<void> {
  const database = await openDatabase();
  try {
    await requestAsPromise(
      database
        .transaction(PROJECTS_STORE, "readwrite")
        .objectStore(PROJECTS_STORE)
        .put(record),
    );
  } catch (error) {
    throw new ProjectPersistenceError("无法保存项目。", { cause: error });
  } finally {
    database.close();
  }
}

export async function deleteProjectRecord(id: string): Promise<void> {
  const database = await openDatabase();
  try {
    await requestAsPromise(
      database
        .transaction(PROJECTS_STORE, "readwrite")
        .objectStore(PROJECTS_STORE)
        .delete(id),
    );
  } finally {
    database.close();
  }
}

/** Save the live editor/chat state into a project id. */
export async function persistActiveProject(
  projectId: string,
  name?: string,
  options: PersistActiveProjectOptions = {},
): Promise<ProjectMeta> {
  if (projectsPendingDeletion.has(projectId)) {
    throw new ProjectPersistenceError("项目正在删除，无法继续保存。");
  }
  const state = useSkinStore.getState();
  if (state.isAiRunning) {
    throw new ProjectPersistenceError("AI 正在绘制，请稍后再保存项目。");
  }
  if (state.strokeBase) state.commitStroke();

  // captureProjectBundle snapshots live stores before its first await.
  const bundlePromise = captureProjectBundle();
  void bundlePromise.catch(() => undefined);

  return enqueueProjectPersist(projectId, async () => {
    const [existing, bundle] = await Promise.all([
      getProject(projectId),
      bundlePromise,
    ]);
    const now = Date.now();
    const record: ProjectRecord = {
      id: projectId,
      name: name?.trim() || existing?.name || "未命名项目",
      createdAt: existing?.createdAt ?? now,
      updatedAt: now,
      json: serializeProjectBundle(bundle),
    };
    await saveProjectRecord(record);
    if (options.activate !== false) {
      setActiveProjectId(projectId);
    }
    return {
      id: record.id,
      name: record.name,
      createdAt: record.createdAt,
      updatedAt: record.updatedAt,
    };
  });
}

export async function loadProjectIntoApp(
  projectId: string,
): Promise<ProjectMeta> {
  const record = await getProject(projectId);
  if (!record) throw new ProjectPersistenceError("找不到该项目。");
  const snapshot = await parseProjectBundleJson(record.json);
  applyProjectSnapshot(snapshot);
  setActiveProjectId(projectId);
  return {
    id: record.id,
    name: record.name,
    createdAt: record.createdAt,
    updatedAt: record.updatedAt,
  };
}

export type CreateProjectOptions = {
  name?: string;
  /** blank | defaultClassic | defaultSlim | fromCurrent */
  seed?: "blank" | "defaultClassic" | "defaultSlim" | "fromCurrent";
  model?: "classic" | "slim";
};

export async function createProject(
  options: CreateProjectOptions = {},
): Promise<ProjectMeta> {
  const id = createId();
  const now = Date.now();
  const name = options.name?.trim() || defaultProjectName(now);
  const seed = options.seed ?? "defaultClassic";
  const previousActiveId = getActiveProjectId();
  setActiveProjectId(id);
  try {
    if (seed === "fromCurrent") {
      return await persistActiveProject(id, name);
    }

    // Seed a clean document into the app, then persist as the new project.
    if (seed === "blank") {
      useSkinStore
        .getState()
        .loadDocument(createBlankSkin(options.model ?? "classic"));
    } else if (seed === "defaultSlim") {
      useSkinStore.getState().loadDocument(createDefaultSkin("slim"));
    } else {
      useSkinStore.getState().loadDocument(createDefaultSkin("classic"));
    }
    useChatSessionStore.getState().clear();
    useSkinStore.setState({
      history: [],
      future: [],
      strokeBase: null,
      aiTransactionBase: null,
      aiTransactionSessionId: null,
      isAiRunning: false,
    });

    return await persistActiveProject(id, name);
  } catch (error) {
    await rollbackFailedProject(id, previousActiveId, seed !== "fromCurrent");
    throw error;
  }
}

export async function renameProject(
  projectId: string,
  name: string,
): Promise<ProjectMeta> {
  const trimmed = name.trim();
  if (!trimmed) throw new ProjectPersistenceError("项目名称不能为空。");
  if (projectsPendingDeletion.has(projectId)) {
    throw new ProjectPersistenceError("项目正在删除，无法重命名。");
  }
  return enqueueProjectPersist(projectId, async () => {
    const existing = await getProject(projectId);
    if (!existing) throw new ProjectPersistenceError("找不到该项目。");
    const record: ProjectRecord = {
      ...existing,
      name: trimmed,
      updatedAt: Date.now(),
    };
    await saveProjectRecord(record);
    return {
      id: record.id,
      name: record.name,
      createdAt: record.createdAt,
      updatedAt: record.updatedAt,
    };
  });
}

export async function deleteProject(projectId: string): Promise<{
  nextActiveId: string | null;
}> {
  const projects = await listProjects();
  if (projects.length <= 1) {
    throw new ProjectPersistenceError("至少保留一个项目。");
  }
  projectsPendingDeletion.add(projectId);
  try {
    await enqueueProjectPersist(projectId, () =>
      deleteProjectRecord(projectId),
    );
  } catch (error) {
    projectsPendingDeletion.delete(projectId);
    throw error;
  }
  const remaining = projects.filter((project) => project.id !== projectId);
  const active = getActiveProjectId();
  if (active === projectId) {
    const next = remaining[0] ?? null;
    if (next) {
      await loadProjectIntoApp(next.id);
      return { nextActiveId: next.id };
    }
    setActiveProjectId("");
    return { nextActiveId: null };
  }
  return { nextActiveId: active };
}

/**
 * Switch project: flush current → load target.
 */
export async function switchProject(
  targetId: string,
  currentId: string | null,
): Promise<ProjectMeta> {
  if (currentId && currentId === targetId) {
    const existing = await getProject(targetId);
    if (!existing) throw new ProjectPersistenceError("找不到该项目。");
    return {
      id: existing.id,
      name: existing.name,
      createdAt: existing.createdAt,
      updatedAt: existing.updatedAt,
    };
  }

  if (useSkinStore.getState().isAiRunning) {
    throw new ProjectPersistenceError("AI 正在绘制，请先停止再切换项目。");
  }

  if (currentId) {
    try {
      await persistActiveProject(currentId);
    } catch {
      // Still attempt switch if save fails? Prefer fail closed.
      throw new ProjectPersistenceError("无法保存当前项目，已取消切换。");
    }
  }

  return loadProjectIntoApp(targetId);
}

/**
 * Import a project file as a brand-new project and switch to it.
 */
export async function importFileAsNewProject(
  file: File,
  name?: string,
): Promise<ProjectMeta> {
  const text = await file.text();
  const snapshot = await parseProjectBundleJson(text);
  const id = createId();
  const now = Date.now();
  const projectName =
    name?.trim() ||
    file.name.replace(/\.json$/i, "").replace(/^skinpilot-project-/, "") ||
    defaultProjectName(now);

  const previousActiveId = getActiveProjectId();
  setActiveProjectId(id);
  try {
    // The active id changes before the live stores, keeping autosave aligned.
    applyProjectSnapshot(snapshot);
    return await persistActiveProject(id, projectName);
  } catch (error) {
    await rollbackFailedProject(id, previousActiveId, true);
    throw error;
  }
}

/**
 * Bootstrap multi-project storage: migrate legacy single workspace if needed,
 * ensure at least one project exists, load active project.
 */
export async function hydrateProjectsFromStorage(): Promise<{
  projects: ProjectMeta[];
  active: ProjectMeta;
  restored: boolean;
}> {
  await migrateLegacyWorkspaceIfNeeded();

  let projects = await listProjects();
  if (projects.length === 0) {
    const created = await createProject({
      name: "默认项目",
      seed: "defaultClassic",
    });
    projects = [created];
    return { projects, active: created, restored: false };
  }

  const preferred = getActiveProjectId();
  const activeMeta =
    projects.find((project) => project.id === preferred) ?? projects[0];

  await loadProjectIntoApp(activeMeta.id);
  return {
    projects: await listProjects(),
    active: activeMeta,
    restored: true,
  };
}

async function migrateLegacyWorkspaceIfNeeded(): Promise<void> {
  const existing = await listProjects();
  if (existing.length > 0) return;

  // Prefer full workspace-v1 snapshot.
  try {
    const legacy = await readLegacyWorkspaceRecord();
    if (legacy?.json) {
      const snapshot = await parseProjectBundleJson(legacy.json);
      applyProjectSnapshot(snapshot);
      const id = createId();
      const now = legacy.updatedAt || Date.now();
      const bundle = await captureProjectBundle();
      await saveProjectRecord({
        id,
        name: "默认项目",
        createdAt: now,
        updatedAt: now,
        json: serializeProjectBundle(bundle),
      });
      setActiveProjectId(id);
      await deleteLegacyWorkspaceKey().catch(() => undefined);
      return;
    }
  } catch {
    // Fall through to draft migration.
  }

  try {
    const draft = await restoreDraft();
    if (draft) {
      useSkinStore.getState().loadDocument(draft.document);
      useSkinStore.setState({
        selectedSurface:
          draft.metadata.selectedSurface ??
          useSkinStore.getState().selectedSurface,
        activeLayer:
          draft.metadata.activeLayer ??
          draft.metadata.selectedSurface?.layer ??
          "inner",
        editorMode: draft.metadata.editorMode ?? "part",
        history: [],
        future: [],
      });
      useChatSessionStore.getState().clear();
      const created = await createProject({
        name: "默认项目",
        seed: "fromCurrent",
      });
      setActiveProjectId(created.id);
    }
  } catch {
    // No legacy data.
  }
}

async function readLegacyWorkspaceRecord(): Promise<{
  json: string;
  updatedAt: number;
} | null> {
  const database = await openDatabase();
  try {
    if (!database.objectStoreNames.contains(WORKSPACE_STORE)) return null;
    const record = await requestAsPromise<
      { id: string; json?: string; updatedAt?: number } | undefined
    >(
      database
        .transaction(WORKSPACE_STORE, "readonly")
        .objectStore(WORKSPACE_STORE)
        .get(WORKSPACE_KEY),
    );
    if (!record?.json) return null;
    return { json: record.json, updatedAt: record.updatedAt ?? Date.now() };
  } finally {
    database.close();
  }
}

async function deleteLegacyWorkspaceKey(): Promise<void> {
  const database = await openDatabase();
  try {
    if (!database.objectStoreNames.contains(WORKSPACE_STORE)) return;
    await requestAsPromise(
      database
        .transaction(WORKSPACE_STORE, "readwrite")
        .objectStore(WORKSPACE_STORE)
        .delete(WORKSPACE_KEY),
    );
  } finally {
    database.close();
  }
}

function defaultProjectName(now: number): string {
  const date = new Date(now);
  const stamp = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  return `项目 ${stamp}`;
}

function openDatabase(): Promise<IDBDatabase> {
  if (typeof indexedDB === "undefined") {
    return Promise.reject(
      new ProjectPersistenceError("当前浏览器不支持 IndexedDB。"),
    );
  }
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(DRAFTS_STORE)) {
        database.createObjectStore(DRAFTS_STORE, { keyPath: "id" });
      }
      if (!database.objectStoreNames.contains(WORKSPACE_STORE)) {
        database.createObjectStore(WORKSPACE_STORE, { keyPath: "id" });
      }
      if (!database.objectStoreNames.contains(PROJECTS_STORE)) {
        database.createObjectStore(PROJECTS_STORE, { keyPath: "id" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(
        new ProjectPersistenceError("无法打开项目数据库。", {
          cause: request.error,
        }),
      );
    request.onblocked = () =>
      reject(new ProjectPersistenceError("项目数据库正被其他页面占用。"));
  });
}

function requestAsPromise<T = IDBValidKey>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// Re-export error name used by workspace for type compatibility.
void WorkspacePersistenceError;
